import React, { useEffect, useRef } from "react";
import axios from "axios"; // TODO - Components which are used by admin as well need to import axios at the moment
import PropTypes from "prop-types"
import AsyncSelect from 'react-select/async';
import throttle from 'lodash/throttle';

import { theme, styles } from "../SelectInput";
import API from "../../../Common/API";

const REQUEST_THROTTLE_TIME = 707;

/**
 * An attempt to make a generic component for a select box for a server resource.
 * Ideally would replace separate components like BlockSelector, UserSelector etc
 * @param props
 * @returns {JSX.Element}
 * @constructor
 * @todo - Needs to work with multi select
 */
const ResourceSelector = (props) => {
    const { multi, onChange, selected } = props;

    const [ selectedOption, setSelectedOption ] = React.useState(null)
    const [ localOptions, setLocalOptions ] = React.useState([]);

    const axiosCancelTokenRef = useRef(null);
    axiosCancelTokenRef.current = axios.CancelToken.source();

    useEffect(() => {
        return () => {
            if (axiosCancelTokenRef.current) {
                // Cancel axios request on unmount
                axiosCancelTokenRef.current.cancel()
            }
        };
    }, []);

    useEffect(() => {
        const foundOption = findOptionFromValue();
        setSelectedOption(foundOption);
    }, [props.value, localOptions])

    // Whenever the options change, maybe autoselect the first option
    useEffect(() => {
        if (!localOptions.length || props.value) {
            return;
        }

        // We might want to autoselect when there's a single option, but we might
        // also be adding a 'listLabel' to the start of the list, so we'll expect
        // 2 options in the list under those circumstances
        const singleOptionExpectedCount = (props.listLabel) ? 2 : 1;

        if (props.autoSelectFirstOption
            || (props.autoSelectSingleOption && localOptions.length === singleOptionExpectedCount)
        ) {
            const firstOption = localOptions.find(option => !option.disabled);

            if (firstOption) {
                setSelectedOption(firstOption);
                if (onChange) {
                    onChange(props.name, firstOption.value)
                }
            }
        }
    }, [localOptions]);

    const findOptionFromValue = (value) => {
        return localOptions.find(option => option.value === props.value);
    }

    const handleChange = (vals, meta) => {
        let newValue = (vals === null ? [] : vals);

        // Hack so that we can specify an option like:
        // {value: undefined, label: "All records"}
        // which will revert to the placeholder if selected
        if (typeof newValue.value === 'undefined') {
            newValue.value = null;
        }

        setSelectedOption(newValue)
        onChange(props.name, newValue.value);
    }

    const promiseOptions = React.useCallback(
        throttle((inputValue, callback) => {

            let queryParams = {
                search: inputValue,
                ...props.queryParams,
            };

            let url = `components/select/${props.type}/${props.action}`;

            if (props.entity) {
                url += `/${props.entity}`
            }

            axios
                .get(
                    API.getUrl(props.module, url),
                    { params: queryParams, }
                )
                .then((res) => {
                    const results = res.data.results ? res.data.results : res.data.data.results;

                    let updatedOptions = props.staticOptions
                        ? [...props.staticOptions, ...results]
                        : results;

                    // Optionally add a message as the first option, which will
                    // be styled differently and won't be selectable
                    if (updatedOptions.length && props.listLabel) {
                        const listLabelOption = {
                            value: '__message',
                            label: props.listLabel,
                            isMessage: true,
                            disabled: true,
                        };
                        updatedOptions = [listLabelOption, ...updatedOptions];
                    }

                    setLocalOptions(updatedOptions);
                    callback(updatedOptions);
                })
                .catch((error) => {
                    console.log('Resource selector request cancelled', error);
                })
            ;
        }, REQUEST_THROTTLE_TIME),
        [props.queryParams, props.module, props.type, props.staticOptions, props.listLabel]
    );

    const formatOptionLabel = (option, {context}) => {
        if (context === 'menu' && option.isMessage) {
            return (
                <div style={{ fontSize: '14px', color: '#999' }}>
                    {option.label}
                </div>
            );
        }
        return option.label;
    }

    return (
        <AsyncSelect
            key={JSON.stringify(props.queryParams)} // Hack to get options to reload when query params are changed
            className={`react-select-container ${(props.disabled) ? 'disabled' : ''}`}
            isMulti={multi}
            defaultOptions={true}
            loadOptions={promiseOptions}
            onChange={handleChange}
            value={selectedOption}
            placeholder={props.placeholder}
            styles={styles}
            theme={theme}
            isDisabled={props.disabled}
            refreshToken={props.queryParams}
            formatOptionLabel={formatOptionLabel}
            isOptionDisabled={(option) => !!option.disabled}
            isClearable={props.isClearable}
        />
    )
}

ResourceSelector.propTypes = {
    type: PropTypes.string.isRequired,
    module: PropTypes.string.isRequired,
    action: PropTypes.string,
    entity: PropTypes.string,
    onChange: PropTypes.func,
    multi: PropTypes.bool,
    selected: PropTypes.array,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    autoSelectFirstOption: PropTypes.bool,
    autoSelectSingleOption: PropTypes.bool,
    queryParams: PropTypes.object,
    staticOptions: PropTypes.array,
    isClearable: PropTypes.bool,
    listLabel: PropTypes.string,
}

ResourceSelector.defaultProps = {
    action: 'add',
    multi: false,
    selected: [],
    placeholder: '',
    autoSelectFirstOption: false,
    autoSelectSingleOption: false,
    queryParams: {},
    staticOptions: [],
    isClearable: false,
    listLabel: null,
}

export default ResourceSelector;
